import {useState} from 'react'
import {PopoverAnchor, PopoverProvider} from '@ariakit/react'
import {useTheme} from 'styled-components'
import {useTranslation} from '@/translations/i18n'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {AlertCircleIcon, CheckCircleIcon, Download01Icon, XCloseIcon} from '@/components/ui/icon/Icon'
import {usePermissions} from '@/features/auth/store/store'
import {hasAnyExportPermission, getEntityLabelKey} from '@/features/exports/utils/entities'
import {selectIndicatorSeverity, useExportsIndicatorStore} from '@/features/exports/hooks/useExportsIndicator'
import {useExportAnnouncementStore} from '@/features/exports/hooks/useExportAnnouncement'
import {downloadExportJob} from '@/features/exports/utils/download'
import {ExportsModal} from '@/features/exports/components/exports-modal/ExportsModal'
import {EXPORTS_ANCHOR_ATTR} from '@/components/commons/export-modal/flyToExports'
import {errorHandler} from '@/utilities/helpers'
import {StyledAnnouncementClose, StyledAnnouncementPopover, StyledIndicatorDot, StyledToggleWrapper} from './style'

/**
 * Globally-accessible control that opens the export-management sidebar.
 * Shown only to users with at least one entity export permission (FR-002a); displays a
 * coloured dot reflecting the most important un-acknowledged export state — pending (orange),
 * ready (green) or failed (red). Opening acknowledges finished results (green/red) but keeps the
 * orange dot while an export is still in progress (FR-002b, extended with per-state colours).
 *
 * Each export also announces itself in a dismissible popover anchored to the toggle: it appears as
 * "in elaborazione" once the enqueue dialog flies in, then switches to a download action (ready) or
 * the failure reason (failed) — replacing the previous transient toast.
 */
export const ExportsToggle = () => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const permissions = usePermissions()
    const [isOpen, setIsOpen] = useState(false)
    const severity = useExportsIndicatorStore(selectIndicatorSeverity)
    const acknowledgeIndicator = useExportsIndicatorStore(state => state.acknowledge)
    const announcement = useExportAnnouncementStore(state => state.job)
    const titleOverrides = useExportAnnouncementStore(state => state.titleOverrides)
    const dismissAnnouncement = useExportAnnouncementStore(state => state.dismiss)

    if (!hasAnyExportPermission(permissions)) return null

    const handleOpen = () => {
        setIsOpen(true)
        acknowledgeIndicator()
        dismissAnnouncement()
    }

    const handleDownload = () => {
        if (announcement) {
            void downloadExportJob(announcement).catch(errorHandler)
        }
        dismissAnnouncement()
    }

    // Popover content derived from the announced job's status: pending/processing → "in elaborazione",
    // ready → download CTA, failed → failure reason.
    const announcementView = (() => {
        if (!announcement) return null
        const entityLabel = t(getEntityLabelKey(announcement.entity))
        // Entities can register a job-specific phrase (e.g. an activity report's period + sector);
        // fall back to the generic "Esportazione pronta/fallita" wording when none was registered.
        const override = titleOverrides[announcement.id]
        switch (announcement.status) {
            case 'ready':
                return {
                    icon: <CheckCircleIcon size={18} fill={palette.success[500]} />,
                    title: `${entityLabel} - ${override?.ready ?? t('exports:toast_ready')}`,
                    description: undefined as string | undefined,
                    showDownload: true
                }
            case 'failed':
                return {
                    icon: <AlertCircleIcon size={18} fill={palette.danger[500]} />,
                    title: `${entityLabel} - ${override?.failed ?? t('exports:toast_failed')}`,
                    description: announcement.failureReason?.message,
                    showDownload: false
                }
            default:
                return {
                    icon: <Spinner size={18} fill={palette.orange[500]} />,
                    title: t('exports:announce_pending_title'),
                    description: t('exports:announce_pending_description'),
                    showDownload: false
                }
        }
    })()

    return (
        <>
            <PopoverProvider
                open={!!announcement}
                setOpen={open => {
                    if (!open) dismissAnnouncement()
                }}
                placement="bottom-end"
            >
                <PopoverAnchor render={<StyledToggleWrapper {...{[EXPORTS_ANCHOR_ATTR]: ''}} />}>
                    <Button variant="ghost" shape="square" onClick={handleOpen} aria-label={t('exports:toggle_label')}>
                        <Download01Icon />
                    </Button>
                    {severity && <StyledIndicatorDot $severity={severity} />}
                </PopoverAnchor>
                <StyledAnnouncementPopover
                    gutter={8}
                    portal
                    unmountOnHide
                    autoFocusOnShow={false}
                    autoFocusOnHide={false}
                    hideOnInteractOutside={false}
                >
                    {announcementView && (
                        <Flexbox direction="column" gap={2} fullWidth>
                            <Flexbox justify="space-between" align="flex-start" gap={2} fullWidth>
                                <Flexbox align="flex-start" gap={2}>
                                    {announcementView.icon}
                                    <Flexbox direction="column" gap={0.5}>
                                        <Text size="textSm" palette="neutral" shade="900" weight="500">
                                            {announcementView.title}
                                        </Text>
                                        {announcementView.description && (
                                            <Text size="textXs" palette="neutral" shade="500">
                                                {announcementView.description}
                                            </Text>
                                        )}
                                    </Flexbox>
                                </Flexbox>
                                <StyledAnnouncementClose
                                    type="button"
                                    onClick={dismissAnnouncement}
                                    aria-label={t('commons:close')}
                                >
                                    <XCloseIcon size={16} fill={palette.neutral[400]} />
                                </StyledAnnouncementClose>
                            </Flexbox>
                            {announcementView.showDownload && (
                                <Button size="xs" variant="ghost" onClick={handleDownload}>
                                    <Download01Icon />
                                    {t('exports:download_to_device')}
                                </Button>
                            )}
                        </Flexbox>
                    )}
                </StyledAnnouncementPopover>
            </PopoverProvider>
            <ExportsModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
        </>
    )
}
